import get from 'lodash/get';
import { SESSION_TIMEOUT_ERROR } from '@xmatters/common/constants/commonStatus';
import * as httpService from '@xmatters/redux/services/httpService';
import { isTimedOut } from '@xmatters/redux/services/sessionService';

const ACTION_SUFFIX = {
  START: '_START',
  SUCCESS: '_SUCCESS',
  FAIL: '_FAIL',
};

const FETCH_METHOD = [
  'get',
  'getall',
  'getallwithembeds',
  'post',
  'del',
  'put',
  'patch',
];

// because _.omit is super slow
const actionWithoutMetaKey = ({ meta, ...otherKeys }) => otherKeys;

export const success =
  ({ type, next, payload, action }) =>
  response =>
    next({
      ...actionWithoutMetaKey(action),
      payload: {
        ...payload,
        ...response,
      },
      type: `${type}${ACTION_SUFFIX.SUCCESS}`,
    });

export const fail =
  ({ type, next, payload, action }) =>
  error => {
    next({
      ...action,
      payload: error,
      initialPayload: payload,
      error: true,
      type: `${type}${ACTION_SUFFIX.FAIL}`,
    });
    throw error;
  };

/**
 * http Checks if the action passed is an xmApi/xmWebApp action (has
 * xmApi/xmWebApp flag and a uri)
 * If so, it calls the httpService and auto dispatches the 'START', 'FAIL' and 'SUCCESS' actions
 * Else, pass the action to the next dispatch
 */
export default function http({ getState }) {
  return next => action => {
    const { type, meta, payload } = action;

    if (!meta) {
      return next(action);
    }

    const {
      xmApi,
      xmWebApp,
      hyrax,
      externalUri,
      uri,
      method,
      params,
      customUriWithParamsGenerator,
      isUriEncoded,
      shouldCallAPI = () => true,
      responseHandler,
      contentType,
      retries,
    } = meta;

    if (
      (xmApi !== true &&
        xmWebApp !== true &&
        hyrax !== true &&
        externalUri !== true) ||
      !uri
    ) {
      return next(action);
    }

    const lowerCasedMethod = method.toLowerCase();

    if (FETCH_METHOD.indexOf(lowerCasedMethod) < 0) {
      throw new Error(`Expected one of: "${FETCH_METHOD.join('", "')}".`);
    }

    const currentState = getState();

    if (!shouldCallAPI(currentState)) {
      return Promise.resolve();
    }

    // if session is timed out, do not make the call
    if ((xmApi && get(currentState, 'session.timeout')) || isTimedOut()) {
      return Promise.reject(new Error(SESSION_TIMEOUT_ERROR)).catch(
        fail({ type, next, payload, action }),
      );
    }

    next({
      ...actionWithoutMetaKey(action),
      payload,
      type: `${type}${ACTION_SUFFIX.START}`,
    });

    return httpService[lowerCasedMethod]({
      uri,
      params,
      customUriWithParamsGenerator,
      responseHandler,
      isUriEncoded,
      contentType,
      retries,
    })
      .then(
        success({ type, next, payload, action }),
        fail({ type, next, payload, action }),
      )
      .catch(e => {
        // Errors thrown in the reducers dispatched in `.then` are swallowed.
        // Catch and rethrow them:
        throw e;
      });
  };
}
